/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {
  ANIMATION_NAMES,
  EventListener,
  EVENTS,
  EventType,
  IFloatingWindow,
  FloatingWindowParams,
  TargetPoint,
  TargetRect,
  StringXPosition,
  StringYPosition,
  stateCallback
} from "./IFloatingWindowConstants";

class FloatingWindow {
  id;
  _targetRect;
  _targetPoint;
  _isDraggable;
  _component;
  _margin;
  events = {};
  _isCloseBtnVisible = true;
  _isScrollable = false;
  destroyWindow;
  onDestroyWindow;

  constructor({
    id,
    targetRect = null,
    targetPoint = null,
    isDraggable,
    component = null,
    margin,
    destroy,
    onDestroy
  }) {
    this.id = id;
    this._targetRect = targetRect;
    this._targetPoint = targetPoint;
    this._isDraggable = isDraggable;
    this._component = component;
    this._margin = margin;
    this.destroyWindow = destroy;
    this.onDestroyWindow = onDestroy;
  }

  get windowId() {
    return this.id;
  }

  get draggableState() {
    return this._isDraggable;
  }

  get targetRect() {
    return this._targetRect;
  }

  get targetPoint() {
    return this._targetPoint;
  }

  get component() {
    return this._component;
  }

  get margin() {
    return this._margin;
  }

  get closeBtnVisibility() {
    return this._isCloseBtnVisible;
  }

  get scrollState() {
    return this._isScrollable;
  }

  toggleDraggableState(callback) {
    const val = callback(this._isDraggable);
    this._isDraggable = val;
    this.emit(EVENTS.TOGGLE_DRAGGABLE, val);
  }

  toggleCloseBtn(callback) {
    const val = callback(this._isCloseBtnVisible);
    this._isCloseBtnVisible = val;
    this.emit(EVENTS.TOGGLE_CLOSE_BTN, val);
  }

  updateFloatingWindowContent(component) {
    this.emit(EVENTS.UPDATE_COMPONENT, component);
    this._component = component;
  }

  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  removeListener(event, listener) {
    if (!listener) {
      this.events[event] = [];
      return;
    }
    if (this.events[event]) {
      const index = this.events[event].indexOf(listener);
      if (index > -1) {
        this.events[event].splice(index, 1);
      }
    }
  }

  emit(event, ...args) {
    const listeners = this.events[event];
    if (listeners && listeners.length > 0) {
      listeners.forEach(listener => listener(...args));
    }
  }

  show() {
    this.emit(EVENTS.SHOW);
  }

  hide() {
    this.emit(EVENTS.HIDE);
  }

  moveTo(x, y, forceTargetPoint = false) {
    if (!forceTargetPoint && this._targetRect && typeof x === "number" && typeof y === "number") {
      this._targetRect.x = x;
      this._targetRect.y = y;
    } else if (this._targetPoint || forceTargetPoint) {
      this._targetPoint.x = x;
      this._targetPoint.y = y;
    } else {
      throw "Invalid targetRect or targetPoint";
    }
    this.emit(EVENTS.MOVE, x, y);
  }

  drag() {
    this.emit(EVENTS.DRAG);
  }

  dragStart() {
    this.emit(EVENTS.DRAG_START);
  }

  dragEnd() {
    this.emit(EVENTS.DRAG_END);
  }

  triggerAnimation(animationId) {
    switch (animationId) {
      case ANIMATION_NAMES.SHAKE:
        this._shakeAnimation();
        break;
      // Add more cases for other animations
      default:
        console.warn(`Unknown animation: ${animationId}`);
    }
  }

  toggleScroll(callback) {
    const val = callback(this._isScrollable);
    this._isScrollable = val;
    this.emit(EVENTS.TOGGLE_SCROLL, val);
  }

  overrideClassName(newClassName = "") {
    this.emit(EVENTS.CLASS_NAME_OVERRIDE, newClassName);
  }

  overrideCSS(newCSS) {
    this.emit(EVENTS.CSS_OVERRIDE, newCSS);
  }

  _shakeAnimation() {
    this.emit(EVENTS.ANIMATION, ANIMATION_NAMES.SHAKE);
  }

  onShow(listener) {
    this.on(EVENTS.SHOW, listener);
  }

  onHide(listener) {
    this.on(EVENTS.HIDE, listener);
  }

  onDragStart(listener) {
    this.on(EVENTS.DRAG_START, listener);
  }

  onDrag(listener) {
    this.on(EVENTS.DRAG, listener);
  }

  onDragEnd(listener) {
    this.on(EVENTS.DRAG_END, listener);
  }

  destroy() {
    if (this.destroyWindow) {
      this.destroyWindow();
    }
  }

  onDestroy(listener) {
    if (this.onDestroyWindow) {
      this.onDestroyWindow(listener);
    }
  }
}

export default FloatingWindow;
